import { useEffect, useRef, useState, useMemo } from "react";
import { mostraAlertaError, mostraAlertaOk } from "../../../componentes/alerts/sweetAlert";
import { AxiosPrivado } from "../../../componentes/axios/Axios";
import { ErroresPeticion } from "../../../componentes/axios/ErroresPeticion";
import Card from "../../../componentes/contenedores/Card";
import { IdentificarEstacion } from "../../../componentes/estacion/Estacion";
import Page from "../../../componentes/plantilla/Page";
import { CajaVerificar, CargaCierre, CierreGuardar, DenominacionListar, UltimaApertura, CierreListar } from "../../../configuracion/apiUrls";
import { useContextUsuario } from "../../../contexto/usuario/UsuarioContext";
import { ImprimirCierre } from "./imprimir/ImprimirCierre";
import { useContextEmpleado } from "../../../contexto/empleado/EmpleadoContext";
import { formatoMoneda } from "../../../configuracion/validaciones";
import Tabla from "../../../componentes/tablas/TablasImprimir";

const HomeCierre = () => {
    const { usuario } = useContextUsuario();
    const { ActualizarLista, setListaCierre } = useContextEmpleado();
    // Estados de Datos
    const [denominaciones, setDenominaciones] = useState([]);
    const [cantidades, setCantidades] = useState({});
    const [ultimaApertura, setUltimaApertura] = useState(null);
    const [cajaId, setCajaId] = useState(null);
    const [cajaAbierta, setCajaAbierta] = useState(null); // null = cargando, true = abierta, false = cerrada
    const [resumenCaja, setResumenCaja] = useState(null);
    const [ingresosEgresos, setIngresosEgresos] = useState([]);

    // Estados de Totales (Mantenidos para el guardado)
    const [montoApertura, setMontoApertura] = useState(0);
    const [ventaContadoEfectivo, setVentaContadoEfectivo] = useState(0);
    const [ventaContadoTarjeta, setVentaContadoTarjeta] = useState(0);
    const [ventaContadoTransferencia, setVentaContadoTransferencia] = useState(0);
    const [ventaCreditoEfectivo, setVentaCreditoEfectivo] = useState(0);
    const [ventaCreditoTarjeta, setVentaCreditoTarjeta] = useState(0);
    const [ventaCreditoTransferencia, setVentaCreditoTransferencia] = useState(0);
    const [totalCreditoVenta, setTotalCreditoVenta] = useState(0);
    const [compraContadoEfectivo, setCompraContadoEfectivo] = useState(0);
    const [compraContadoTarjeta, setCompraContadoTarjeta] = useState(0);
    const [compraContadoTransferencia, setCompraContadoTransferencia] = useState(0);
    const [compraCreditoEfectivo, setCompraCreditoEfectivo] = useState(0);
    const [compraCreditoTarjeta, setCompraCreditoTarjeta] = useState(0);
    const [compraCreditoTransferencia, setCompraCreditoTransferencia] = useState(0);
    const [totalCreditoCompra, setTotalCreditoCompra] = useState(0);

    // Ingresos/Egresos Manuales Totales
    const [ingresoEfectivo, setIngresoEfectivo] = useState(0);
    const [ingresoPos, setIngresoPos] = useState(0);
    const [ingresoTransferencia, setIngresoTransferencia] = useState(0);
    const [egresoEfectivo, setEgresoEfectivo] = useState(0);
    const [egresoPos, setEgresoPos] = useState(0);
    const [egresoTransferencia, setEgresoTransferencia] = useState(0);
    const [totalSistemaIngresos, setTotalSistemaIngresos] = useState(0);
    const [totalSistemaEgresos, setTotalSistemaEgresos] = useState(0);

    // Estados Visuales / Inputs Manuales
    const [totalGeneralEfectivo, setTotalGeneralEfectivo] = useState('');
    const [totalGeneralTarjeta, setTotalGeneralTarjeta] = useState('');
    const [totalGeneralTransferencia, setTotalGeneralTransferencia] = useState('');
    const [mostrarDenominaciones, setMostrarDenominaciones] = useState(false);
    const refDenominaciones = useRef(null);

    const [productosVendidos, setProductosVendidos] = useState([]);

    const verificarYCargarCaja = async () => {
        try {
            const estacionDatos = await IdentificarEstacion();
            const verificationResponse = await AxiosPrivado.post(CajaVerificar, { estacionDatos });

            if (verificationResponse.data.estado === "CE") {
                mostraAlertaError("La caja ya está cerrada. No se puede iniciar un nuevo proceso de cierre.");
                setCajaAbierta(false);
                setCajaId(verificationResponse.data.id || null);
                return;
            }

            const responseCierre = await AxiosPrivado.post(CargaCierre, estacionDatos);
            const { caja, resumen, apertura, movimientosAdicionales, productosVendidos } = responseCierre.data;
            setCajaId(caja.id);
            setResumenCaja(resumen);
            setCajaAbierta(true);
            setMontoApertura(apertura?.efectivo ? parseFloat(apertura.efectivo) : 0);
            setProductosVendidos(
            (productosVendidos || []).map(producto => ({
                ...producto,
                vendedor: usuario.datoPersonales.nombreCorto,
                cajaId: caja.id,
            }))
            );
            if (movimientosAdicionales && Array.isArray(movimientosAdicionales)) {
                setIngresosEgresos(movimientosAdicionales);
            } else if (resumen.ingresosEgresosDetalle && Array.isArray(resumen.ingresosEgresosDetalle)) {
                setIngresosEgresos(resumen.ingresosEgresosDetalle);
            }

            // Carga de Estados para Guardado (Mapeo seguro)
            setVentaContadoEfectivo(resumen.ventas?.contado?.efectivo || 0);
            setVentaContadoTarjeta(resumen.ventas?.contado?.tarjeta || 0);
            setVentaContadoTransferencia(resumen.ventas?.contado?.transferencia || 0);

            setVentaCreditoEfectivo(resumen.abonosCredito?.ventas?.efectivo || 0);
            setVentaCreditoTarjeta(resumen.abonosCredito?.ventas?.tarjeta || 0);
            setVentaCreditoTransferencia(resumen.abonosCredito?.ventas?.transferencia || 0);
            setTotalCreditoVenta(resumen.ventas?.credito?.totalCredito || 0);

            setCompraContadoEfectivo(resumen.compras?.contado?.efectivo || 0);
            setCompraContadoTarjeta(resumen.compras?.contado?.tarjeta || 0);
            setCompraContadoTransferencia(resumen.compras?.contado?.transferencia || 0);

            setCompraCreditoEfectivo(resumen.abonosCredito?.compras?.efectivo || 0);
            setCompraCreditoTarjeta(resumen.abonosCredito?.compras?.tarjeta || 0);
            setCompraCreditoTransferencia(resumen.abonosCredito?.compras?.transferencia || 0);
            setTotalCreditoCompra(resumen.compras?.credito?.totalCredito || 0);

            const ie = resumen.ingresosEgresos;
            if (ie) {
                // CORRECCIÓN: Usar keys correctas del JSON (tarjeta en vez de Pos)
                setIngresoEfectivo(ie.Ingreso?.efectivo || 0);
                setIngresoPos(ie.Ingreso?.tarjeta || 0);
                setIngresoTransferencia(ie.Ingreso?.transferencia || 0);

                setEgresoEfectivo(ie.Egreso?.efectivo || 0);
                setEgresoPos(ie.Egreso?.tarjeta || 0);
                setEgresoTransferencia(ie.Egreso?.transferencia || 0);

                setTotalSistemaIngresos(ie.totalIngreso || 0);
                setTotalSistemaEgresos(ie.totalEgreso || 0);
            }
        } catch (error) {
            ErroresPeticion(error);
            setCajaAbierta(false);
        }
    };

    useEffect(() => {
        verificarYCargarCaja();

        const fetchUltimaApertura = async () => {
            try {
                const response = await AxiosPrivado.get(UltimaApertura);
                setUltimaApertura(response.data);
            } catch (error) {
                console.error("Error al obtener la última apertura:", error);
            }
        };
        fetchUltimaApertura();

        const fetchDenominaciones = async () => {
            try {
                const response = await AxiosPrivado.get(DenominacionListar);
                const datos = response.data;
                const cantidadesIniciales = {};
                datos.forEach((denom) => {
                    cantidadesIniciales[denom.id] = "";
                });
                setDenominaciones(datos);
                setCantidades(cantidadesIniciales);
            } catch (error) {
                console.error("Error cargando las denominaciones:", error);
            }
        };
        fetchDenominaciones();
    }, []);

    const handleChange = (id, cantidad) => {
        const cantidadLimpia = cantidad === "" ? "" : Math.max(0, parseInt(cantidad, 10));
        setCantidades((prev) => ({
            ...prev,
            [id]: cantidadLimpia,
        }));
    };

    
    
    const columnas = useMemo(
        () => [
            {
                title: "ID",
                data: "productoId",
            },
            {
                title: "Codigo",
                data: "producto",
                render: (rowData) => {
                    const producto = rowData.codigo; // Asegúrate de que 'cliente' esté en 'rowData'
                    if (!producto) return "Sin codigo";

                    const productoNombreHtml = producto
                        ? `<div>${producto}</div>`
                        : "";

                    return productoNombreHtml;
                },
            },
            {
                title: "Nombre",
                data: "producto",
                render: (rowData) => {
                    const producto = rowData.nombre; // Asegúrate de que 'cliente' esté en 'rowData'
                    if (!producto) return "Sin producto";

                    const productoNombreHtml = producto
                        ? `<div>${producto}</div>`
                        : "";

                    return productoNombreHtml;
                },
            },
            {
                title: "Cantidad Vendida",
                data: "cantidadVendida",
                render: (data) => {
                    const valorFormateado = data;
                    return `<span style="text-align: right; display: block;">${valorFormateado}</span>`;
                }
            },
            {
                title: "Vendedor",
                data: "vendedor",
                render: (data) => {
                    return `<div">${data}</div>`;
                }
            },
        ],
        []
    );


    const calcularTotalDenominacion = (id) => {
        const denominacion = denominaciones.find((d) => d.id === id);
        return (parseInt(cantidades[id]) || 0) * (denominacion?.valor || 0);
    };

    const totalContadoFisicoDenominaciones = denominaciones.reduce(
        (sum, denom) => sum + calcularTotalDenominacion(denom.id),
        0
    );

    const valorFisicoEfectivo = parseFloat(totalGeneralEfectivo) || 0;
    const valorFisicoTarjeta = parseFloat(totalGeneralTarjeta) || 0;
    const valorFisicoTransferencia = parseFloat(totalGeneralTransferencia) || 0;

    const diferenciaEfectivo = valorFisicoEfectivo - totalContadoFisicoDenominaciones;

    const guardarCierre = async () => {
        if (!cajaId || !ultimaApertura?.id) {
            mostraAlertaError("Caja no autenticada o apertura no disponible.");
            return;
        }
        if (!cajaAbierta) {
            mostraAlertaError("No se puede guardar cierre: la caja no está abierta.");
            return;
        }
        /*
        if (diferenciaEfectivo !== 0) {
            mostraAlertaError("El valor ingresado en 'Efectivo Contado Físico' no coincide con el total de su Conteo por Denominaciones. Debe ser L 0.00.");
            return;
        }*/

        try {
            const detalleCierre = Object.keys(cantidades)
                .filter((id) => parseInt(cantidades[id]) > 0)
                .map((id) => ({
                    cantidad: parseInt(cantidades[id]),
                    denominacionId: parseInt(id),
                }));

            const totalAbonoCreditoVentas = ventaCreditoEfectivo + ventaCreditoTarjeta + ventaCreditoTransferencia;
            const totalAbonoCreditoCompras = compraCreditoEfectivo + compraCreditoTarjeta + compraCreditoTransferencia;

            const datos = {
                efectivo: valorFisicoEfectivo,
                pos: valorFisicoTarjeta,
                transferencias: valorFisicoTransferencia,
                // Sistema Ventas
                efectivosistema: parseFloat(ventaContadoEfectivo),
                possistema: parseFloat(ventaContadoTarjeta),
                transferenciassistema: parseFloat(ventaContadoTransferencia),
                creditosistema: parseFloat(totalCreditoVenta),
                abonocreditosistema: totalAbonoCreditoVentas,
                totalventa: resumenCaja?.totalValorVentas || 0,

                // Sistema Compras
                efectivocompra: parseFloat(compraContadoEfectivo),
                tarjetacompra: parseFloat(compraContadoTarjeta),
                transferenciascompra: parseFloat(compraContadoTransferencia),
                creditocomprasistema: parseFloat(totalCreditoCompra),
                abonocreditocompra: totalAbonoCreditoCompras,
                totalcompra: parseFloat(resumenCaja?.totalValorCompras) || 0,

                anulado: false,
                cajaId,
                aperturaId: ultimaApertura.id,
                ingresosEgresos: ingresosEgresos,
                detalleCierre,
                usuarioId: usuario.id,
            };

            const response = await AxiosPrivado.post(CierreGuardar, datos);
            mostraAlertaOk("Cierre guardado correctamente");
            console.log(response.data);

            const cierreGuardado = response.data;
            const ingresosEgresosResponse = response.data.ingresosEgresos;
            const datosParaImprimir = {
                nuevoCierre: cierreGuardado,
                ingresosEgresos: ingresosEgresosResponse || ingresosEgresos
            };

            ImprimirCierre(datosParaImprimir, usuario);
            ActualizarLista(CierreListar, setListaCierre);

            setCantidades({});
            setTotalGeneralEfectivo('');
            setTotalGeneralTarjeta('');
            setTotalGeneralTransferencia('');
            setResumenCaja(null);
            setCajaAbierta(false);
        } catch (error) {
            mostraAlertaError("Error al guardar el cierre");
            console.error(error);
        }
    };


    const CardResumenSimple = ({ titulo, resumen, tipo }) => {
        if (!resumen || !resumen.ventas || !resumen.compras) return null;
        const colorHeader = tipo === 'Ventas' ? 'bg-success' : 'bg-danger';
        const totalCalculado = tipo === 'Ventas'
            ? (parseFloat(resumen.ventas.totalContado || 0) + parseFloat(resumen.ventas.credito.totalCredito || 0))
            : (parseFloat(resumen.compras.totalContado || 0) + parseFloat(resumen.compras.credito.totalCredito || 0));

        const montoContado = tipo === 'Ventas' ? resumen.ventas.totalContado : resumen.compras.totalContado;
        const montoCredito = tipo === 'Ventas' ? resumen.ventas.credito.totalCredito : resumen.compras.credito.totalCredito;

        return (
            <div className="card mb-3 shadow-sm border border-secondary">
                <div className={`card-header text-white py-2 ${colorHeader}`}>
                    <h6 className="mb-0 fw-bold text-uppercase">
                        <i className={`fas ${tipo === 'Ventas' ? 'fa-shopping-bag mx-2' : 'fa-shopping-cart mx-2'} me-2`}></i>
                        {titulo}
                    </h6>
                </div>
                <div className="card-body p-0">
                    <table className="table table-sm table-striped mb-0">
                        <tbody>
                            <tr>
                                <td>Contado</td>
                                <td className="text-end">{formatoMoneda(montoContado)}</td>
                            </tr>
                            <tr>
                                <td>Crédito (Valor original)</td>
                                <td className="text-end">{formatoMoneda(montoCredito)}</td>
                            </tr>
                        </tbody>
                        <tfoot className="fw-bold border-top">
                            <tr>
                                <td>Total</td>
                                <td className={`text-end ${tipo === 'Ventas' ? 'text-success' : 'text-danger'}`}>
                                    {formatoMoneda(totalCalculado)}
                                </td>
                            </tr>
                        </tfoot>
                    </table>
                </div>
            </div>
        );
    };

    const CardDetalleFlujo = ({ titulo, tipoPago, resumenCaja, montoApertura }) => {
        if (!resumenCaja) return null;

        const keyIE = tipoPago;
        const isEfectivo = tipoPago === 'efectivo';

        const apertura = isEfectivo ? (parseFloat(montoApertura) || 0) : 0;
        const ventasContado = resumenCaja.ventas?.contado?.[tipoPago] || 0;
        const abonosVentas = resumenCaja.abonosCredito?.ventas?.[tipoPago] || 0;
        const otrosIngresos = resumenCaja.ingresosEgresos?.Ingreso?.[keyIE] || 0;

        const comprasContado = resumenCaja.compras?.contado?.[tipoPago] || 0;
        const abonosCompras = resumenCaja.abonosCredito?.compras?.[tipoPago] || 0;
        const otrosEgresos = resumenCaja.ingresosEgresos?.Egreso?.[keyIE] || 0;

        const totalIngresos = apertura + ventasContado + abonosVentas + otrosIngresos;
        const totalEgresos = comprasContado + abonosCompras + otrosEgresos;
        const saldoFinal = totalIngresos - totalEgresos;

        const config = {
            efectivo: { color: 'primary', icon: 'fa-money-bill-wave', label: 'Efectivo' },
            tarjeta: { color: 'info', icon: 'fa-credit-card', label: 'POS / Tarjeta' },
            transferencia: { color: 'warning', icon: 'fa-exchange-alt', label: 'Transferencia' }
        }[tipoPago];

        return (
            <div className="card mb-3 shadow-sm border-0">
                <div className={`card-header bg-${config.color} text-white py-2`}>
                    <h6 className="mb-0 fw-bold text-uppercase">
                        <i className={`fas ${config.icon} me-2`}></i> {titulo}
                    </h6>
                </div>
                <div className="card-body p-0">
                    <table className="table table-sm table-hover mb-0" style={{ fontSize: '0.9rem' }}>
                        <thead className="table-light">
                            <tr>
                                <th>Concepto</th>
                                <th className="text-end text-success">Ingreso (+)</th>
                                <th className="text-end text-danger">Egreso (-)</th>
                            </tr>
                        </thead>
                        <tbody>
                            {isEfectivo && (
                                <tr>
                                    <td>Apertura Caja</td>
                                    <td className="text-end">{formatoMoneda(apertura)}</td>
                                    <td className="text-end">-</td>
                                </tr>
                            )}
                            <tr>
                                <td>Ventas / Compras</td>
                                <td className="text-end">{formatoMoneda(ventasContado)}</td>
                                <td className="text-end">{formatoMoneda(comprasContado)}</td>
                            </tr>
                            <tr>
                                <td>Abonos Créditos</td>
                                <td className="text-end">{formatoMoneda(abonosVentas)}</td>
                                <td className="text-end">{formatoMoneda(abonosCompras)}</td>
                            </tr>
                            <tr>
                                <td>Entradas / Salidas</td>
                                <td className="text-end">{formatoMoneda(otrosIngresos)}</td>
                                <td className="text-end">{formatoMoneda(otrosEgresos)}</td>
                            </tr>
                        </tbody>
                        <tfoot className="table-light border-top">
                            <tr>
                                <td className="fw-bold">TOTAL {config.label.toUpperCase()}</td>
                                <td colSpan="2" className={`text-end fw-bolder fs-6 ${saldoFinal >= 0 ? 'text-success' : 'text-danger'}`}>
                                    {formatoMoneda(saldoFinal)}
                                </td>
                            </tr>
                        </tfoot>
                    </table>
                </div>
            </div>
        );
    };

    const datos = {
        titulo: {
            titulo: "",
            url: "/app/admin/cajeros/cierre",
            tituloUrl: "Menú Cierre",
            nombreUrl: "Inicio",
        },
    };

    if (cajaAbierta === null) {
        return (
            <Page datos={datos}>
                <div className="d-flex justify-content-center align-items-center" style={{ minHeight: '300px' }}>
                    <div className="text-center">
                        <div className="spinner-border text-primary" role="status"></div>
                        <p className="mt-3">Cargando datos de la caja...</p>
                    </div>
                </div>
            </Page>
        );
    }

    if (cajaAbierta === false && !cajaId) {
        return (
            <Page datos={datos}>
                <div className="alert alert-danger text-center fs-5" role="alert">
                    <i className="fas fa-exclamation-triangle me-2"></i>
                    La caja no está abierta o hubo un error de conexión.
                </div>
            </Page>
        );
    }

    return (
        <Page datos={datos}>
            <Card>
                <div className="d-flex align-items-center justify-content-between border-bottom pb-3 mb-4">
                    <h3 className="fw-bolder text-dark m-0">
                        <i className="fas fa-calculator me-2 text-primary"></i> Informe de Cierre de Caja
                    </h3>
                </div>
                <div className="row mb-3">
                    <div className="col-12">
                        <div className="bg-white border-start border-5 border-primary shadow-sm p-3 rounded-end d-flex justify-content-between align-items-center">
                            <div>
                                <span className="d-block text-secondary small fw-bold text-uppercase mb-1">Estado Inicial</span>
                                <div className="d-flex align-items-center">
                                    <i className="fas fa-door-open text-primary fs-4 me-3"></i>
                                    <span className="fw-bold text-dark fs-5 mx-2">Apertura Caja</span>
                                </div>
                            </div>
                            <span className="fs-4 fw-bolder text-dark bg-light px-3 py-1 rounded border">
                                {formatoMoneda(montoApertura)}
                            </span>
                        </div>
                    </div>
                </div>
                <div className="row">
                    <div className="col-12 col-xl-4 mb-4">
                        <h6 className="fw-bold mb-3 text-primary text-uppercase border-bottom pb-2">
                            <i className="fas fa-laptop-code me-2"></i>Detalle Sistema
                        </h6>
                        <CardDetalleFlujo
                            titulo="Flujo de Efectivo"
                            tipoPago="efectivo"
                            resumenCaja={resumenCaja}
                            montoApertura={montoApertura}
                        />
                        <CardDetalleFlujo
                            titulo="Flujo Transferencias"
                            tipoPago="transferencia"
                            resumenCaja={resumenCaja}
                            montoApertura={montoApertura}
                        />
                        <CardDetalleFlujo
                            titulo="Flujo POS / Tarjeta"
                            tipoPago="tarjeta"
                            resumenCaja={resumenCaja}
                            montoApertura={montoApertura}
                        />
                    </div>
                    <div className="col-12 col-xl-4 mb-4">
                        <h6 className="fw-bold mb-3 text-dark text-uppercase border-bottom pb-2">
                            <i className="fas fa-pen-alt me-2 mx-2"></i>Ingreso de Datos
                        </h6>
                        <div className="card shadow-sm border-0">
                            <div className="card-header bg-white py-3 border-bottom">
                                <h6 className="m-0 fw-bold text-primary">
                                    <i className="fas fa-hand-holding-usd me-2"></i>
                                    Conteo Físico (Manual)
                                </h6>
                            </div>
                            <div className="card-body p-3">
                                <p className="small text-muted mb-4">
                                    Ingrese los montos reales encontrados en caja.
                                </p>
                                <div className="mb-4">
                                    <label className="form-label fw-bold small text-uppercase text-secondary d-flex justify-content-between">
                                        <span>Efectivo</span>
                                        <span className="badge bg-success bg-opacity-10 text-success border border-success border-opacity-25">
                                            {formatoMoneda(totalGeneralEfectivo)}
                                        </span>
                                    </label>
                                    <div className="input-group">
                                        <span className="input-group-text bg-light fw-bold">L</span>
                                        <input
                                            type="number"
                                            className="form-control text-end fw-bold fs-5"
                                            placeholder="0.00"
                                            value={totalGeneralEfectivo}
                                            onChange={(e) => setTotalGeneralEfectivo(e.target.value)}
                                            min="0" step="0.01" onWheel={(e) => e.target.blur()}
                                        />
                                    </div>
                                    <div className="d-flex justify-content-between mt-1 small">
                                        <span className="text-muted">Diferencia:</span>
                                        <span className={`fw-bold ${diferenciaEfectivo === 0 ? 'text-success' : 'text-danger'}`}>
                                            {formatoMoneda(diferenciaEfectivo)}
                                        </span>
                                    </div>
                                </div>
                                <div className="mb-3">
                                    <label className="form-label fw-bold small text-uppercase text-secondary d-flex justify-content-between">
                                        <span>POS / Tarjeta</span>
                                        <span className="badge bg-info bg-opacity-10 text-info border border-info border-opacity-25">
                                            {formatoMoneda(totalGeneralTarjeta)}
                                        </span>
                                    </label>
                                    <input
                                        type="number"
                                        className="form-control text-end"
                                        placeholder="0.00"
                                        value={totalGeneralTarjeta}
                                        onChange={(e) => setTotalGeneralTarjeta(e.target.value)}
                                        min="0" step="0.01" onWheel={(e) => e.target.blur()}
                                    />
                                </div>
                                <div className="mb-4">
                                    <label className="form-label fw-bold small text-uppercase text-secondary d-flex justify-content-between">
                                        <span>Transferencia</span>
                                        <span className="badge bg-warning bg-opacity-10 text-warning border border-warning border-opacity-25">
                                            {formatoMoneda(totalGeneralTransferencia)}
                                        </span>
                                    </label>
                                    <input
                                        type="number"
                                        className="form-control text-end"
                                        placeholder="0.00"
                                        value={totalGeneralTransferencia}
                                        onChange={(e) => setTotalGeneralTransferencia(e.target.value)}
                                        min="0" step="0.01" onWheel={(e) => e.target.blur()}
                                    />
                                </div>
                                <div className="d-grid gap-2 mt-4">
                                    <button
                                        className={`btn btn-sm ${mostrarDenominaciones ? 'btn-outline-secondary' : 'btn-outline-primary'} fw-bold`}
                                        onClick={() => {
                                            setMostrarDenominaciones(!mostrarDenominaciones);
                                            if (!mostrarDenominaciones) setTimeout(() => refDenominaciones.current?.scrollIntoView({ behavior: 'smooth' }), 100);
                                        }}
                                    >
                                        {mostrarDenominaciones ? 'Ocultar Billetes/Monedas' : 'Desglosar Billetes/Monedas'}
                                    </button>
                                    <button
                                        className="btn btn-success mx-2"
                                        onClick={guardarCierre}
                                        //disabled={diferenciaEfectivo !== 0}
                                    >
                                        <i className="fas fa-save me-2"></i> Guardar
                                    </button>
                                </div>
                            </div>
                        </div>
                        {mostrarDenominaciones && (
                            <div className="card mt-3 shadow border-0" ref={refDenominaciones}>
                                <div className="card-body p-0">
                                    <div className="table-responsive">
                                        <table className="table table-bordered table-sm mb-0">
                                            <thead className="bg-light text-center">
                                                <tr>
                                                    <th className="small text-uppercase align-middle">Valor</th>
                                                    <th className="small text-uppercase align-middle">Cant.</th>
                                                    <th className="small text-uppercase align-middle">Total</th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {denominaciones.map((denom) => (
                                                    <tr key={denom.id}>
                                                        {/* Valor */}
                                                        <td className="align-middle small fw-semibold text-center text-nowrap">
                                                            {formatoMoneda(denom.valor)}
                                                        </td>

                                                        {/* Input de Cantidad - Usamos text-center en el TD para el input */}
                                                        <td className="p-1 align-middle text-center">
                                                            <input
                                                                type="number" min="0"
                                                                value={cantidades[denom.id] ?? ""}
                                                                onChange={(e) => handleChange(denom.id, e.target.value)}
                                                                // Hacemos el input más pequeño para dejar espacio para la cantidad
                                                                className="form-control form-control-sm text-center fw-bold border-0 bg-light"
                                                                placeholder="0"
                                                                onWheel={(e) => e.target.blur()}
                                                            />
                                                        </td>

                                                        {/* Total por fila - Usamos text-nowrap para que no salte de línea */}
                                                        <td className="text-end align-middle small fw-bold text-primary text-nowrap">
                                                            {formatoMoneda(calcularTotalDenominacion(denom.id))}
                                                        </td>
                                                    </tr>
                                                ))}
                                            </tbody>
                                            <tfoot className="table-light border-top-2">
                                                <tr>
                                                    <td colSpan="2" className="text-end fw-bold small text-uppercase">Total Físico:</td>
                                                    {/* Total Físico - Usamos text-nowrap aquí también */}
                                                    <td className="text-end fw-bold small text-dark text-nowrap">
                                                        {formatoMoneda(totalContadoFisicoDenominaciones)}
                                                    </td>
                                                </tr>
                                            </tfoot>
                                        </table>
                                    </div>
                                </div>
                            </div>
                        )}
                    </div>

                    {/* COLUMNA 3: RESUMEN GENERAL (Derecha) */}
                    <div className="col-12 col-xl-4 mb-4">
                        <h6 className="fw-bold mb-3 text-muted text-uppercase border-bottom pb-2">
                            <i className="fas fa-chart-pie me-2"></i>Resumen General
                        </h6>
                        <CardResumenSimple titulo="Ventas Totales" resumen={resumenCaja} tipo="Ventas" />
                        <CardResumenSimple titulo="Compras Totales" resumen={resumenCaja} tipo="Compras" />

                        <div className="card mb-3 shadow-sm border border-secondary">
                            <div className="card-header bg-secondary text-white py-2">
                                <h6 className="mb-0 fw-bold text-uppercase">Manuales (Ing/Egr)</h6>
                            </div>
                            <div className="card-body p-3 small">
                                <div className="d-flex justify-content-between fw-bold text-success border-bottom pb-1 mb-1">
                                    <span>Total Ingresos:</span>
                                    <span>{formatoMoneda(totalSistemaIngresos)}</span>
                                </div>
                                <div className="ps-2 text-muted mb-3 d-flex justify-content-between">
                                    <span>Efe: {formatoMoneda(ingresoEfectivo)}</span>
                                    <span>Pos: {formatoMoneda(ingresoPos)}</span>
                                    <span>Trx: {formatoMoneda(ingresoTransferencia)}</span>
                                </div>

                                <div className="d-flex justify-content-between fw-bold text-danger border-bottom pb-1 mb-1">
                                    <span>Total Egresos:</span>
                                    <span>{formatoMoneda(totalSistemaEgresos)}</span>
                                </div>
                                <div className="ps-2 text-muted d-flex justify-content-between">
                                    <span>Efe: {formatoMoneda(egresoEfectivo)}</span>
                                    <span>Pos: {formatoMoneda(egresoPos)}</span>
                                    <span>Trx: {formatoMoneda(egresoTransferencia)}</span>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </Card>
            <Card titulo="Productos vendidos en el cierre" className="mt-4">
                {/* Productos vendidos */}
                <Tabla
                    datos={productosVendidos}
                    columnas={columnas}
                    id="tablaProductosVendidos"
                >

                </Tabla>
            </Card>
        </Page>
    );
};

export default HomeCierre;